<style lang="postcss">
	.form-footer {
		gap: 1rem;
		display: flex;
		align-items: center;
		flex-direction: row;
		container: form-footer / inline-size;
		justify-content: flex-start;
		width: 100%;
	}

	.form-footer.is-col {
		flex-direction: column;
	}

	.form-footer-info {
		display: flex;
		flex-direction: row;
		align-items: center;
		font-weight: 480;
		opacity: 0.65;
	}

	.container {
		display: flex;
		width: 100%;
		align-items: center;
		gap: 1rem;
	}

	.has-separator::before {
		content: "•";
		margin: 0 0.5rem;
	}

	.icon-path {
		fill: none;
		stroke: currentColor;
		stroke-linecap: round;
		stroke-linejoin: round;
		stroke-width: 2;
	}

	.hidden-button {
		visibility: hidden;
		display: none;
	}

	.next-button {
		display: flex;
		flex-direction: row;
		align-items: center;
		height: 100%;
		gap: 0.25rem;
	}

	.next-content {
		display: flex;
		flex-direction: row;
		align-items: center;
	}

	.icon-wrapper {
		width: 1.25rem;
		height: 1rem;
		display: flex;
		align-items: center;
		justify-content: center;
		margin-left: 0.25rem;
	}
</style>

<script lang="ts">
	import { getContext } from "svelte";
	import type { Writable } from "svelte/store";
	import { slide } from "$lib/transitions";

	import type { t as tBase } from "$lib/helpers/translate";
	const t = getContext<typeof tBase>("t");

	export let lastSlide: boolean;
	export let navigate: (direction: number) => void;
	export let currentPageId: string;
	export let pages: PageDisplay[];
	export let formLoading: boolean;
	export let powered_by: string | null = null;

	let formMeta = getContext<Writable<FormMeta>>("formMeta");
	let uploadsInProgress =
		getContext<Writable<{ [key: string]: number }>>("uploadsInProgress");

	const lookupLoading = getContext<Writable<string[]>>("lookupLoading");
	const styling = getContext<Styling>("styling");
	const config = getContext<Config>("config");
	const saveProgressMessage = getContext<Writable<string>>(
		"saveProgressMessage"
	);
	const iframe = getContext<boolean>("iframe");
	const seamless = getContext<boolean>("seamless");

	$: isLookupLoading = $lookupLoading.filter(Boolean).length > 0;
	$: areUploadsInProgress =
		Object.values($uploadsInProgress).reduce((acc, val) => acc + val, 0) > 0;
	$: visiblePages = pages.filter((page) => page.visibleComputed);
	$: indexAmongVisible = visiblePages.findIndex(
		(page) => page.id === currentPageId
	);
	$: hideSubmitButton =
		currentPageId !== "welcome" &&
		lastSlide &&
		$formMeta.submitButtonVisible === false;
</script>

{#if hideSubmitButton === false}
	<div
		class="form-footer {$$props.class || ''}"
		class:is-col="{styling.wideSubmit && pages.length === 1}">
		{#if lastSlide}
			{#if $formMeta.submitButtonVisible}
				<button
					type="submit"
					style="width: {styling.wideSubmit && pages.length === 1
						? '100%'
						: 'auto'}"
					disabled="{$formMeta.formDisabled ||
						formLoading ||
						isLookupLoading ||
						areUploadsInProgress}"
					class="button">
					{@html $formMeta.submitButtonText}
					{#if formLoading || isLookupLoading}
						<span transition:slide="{{ axis: 'x' }}" class="loading-indicator">
							<span
								class="loader"
								style="width: 0.75rem; height: 0.75rem; display: inline-block; margin-left: 0.5rem;"
							></span>
						</span>
					{/if}
				</button>
				<div style="display: none">
					<input
						name="phone_number"
						tabindex="-1"
						type="text"
						value=""
						autocomplete="new-password" />
				</div>
			{/if}
		{:else}
			<button
				on:click="{() => navigate(+1)}"
				type="button"
				disabled="{$formMeta.formDisabled ||
					isLookupLoading ||
					areUploadsInProgress}"
				class="button next-button">
				<div class="next-content">
					{t.get("next")}
					<div
						class="icon-wrapper font-initial"
						style="width: 1rem; height: 1rem; font-weight: 400">
						{#if isLookupLoading}
							<div class="loader"></div>
						{:else}
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="0.95rem"
								height="0.95rem"
								fill="currentColor"
								viewBox="0 0 256 256"
								><path
									d="M224.49,136.49l-72,72a12,12,0,0,1-17-17L187,140H40a12,12,0,0,1,0-24H187L135.51,64.48a12,12,0,0,1,17-17l72,72A12,12,0,0,1,224.49,136.49Z"
								></path
								></svg>
						{/if}
					</div>
				</div>
			</button>
			<button type="submit" disabled class="hidden-button"></button>
		{/if}
		<div class="form-footer-info">
			{#if pages.length > 1}
				{#if isLookupLoading}
					<span class="page-info">{t.get("loading")}</span>
				{:else}
					<span class="page-info"
						>{indexAmongVisible + 1} / {visiblePages.length}</span>
				{/if}
			{/if}
			{#if config.saveProgress && config.saveProgressMessage && iframe && seamless && $saveProgressMessage && pages.length > 1}
				<div class="page-info has-separator">
					{$saveProgressMessage}
				</div>
			{/if}
			{#if powered_by}
				<a
					href="{powered_by}"
					target="_blank"
					class="page-info has-separator"
					style="letter-spacing: -0.2px">
					{@html t
						.get("powered_by")
						.replace("{0}", '<span style="font-weight: 600">formcrafts</span>')}
				</a>
			{/if}
		</div>
	</div>
{/if}
